import React, { forwardRef } from 'react'

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
  required?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  required,
  id,
  className = '',
  ...props
}, ref) => {
  return (
    <div>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-foreground mb-1">
          {label}
          {required && <span className="text-error ml-0.5">*</span>}
        </label>
      )}
      <input
        ref={ref}
        id={id}
        className={`
          block w-full px-3 py-2 rounded border border-border
          text-foreground placeholder-foreground-muted
          bg-surface
          focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
          disabled:bg-background-alt disabled:cursor-not-allowed
          sm:text-sm
          ${error ? 'border-error focus:ring-error focus:border-error' : ''}
          ${className}
        `}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-error">{error}</p>}
      {helperText && !error && <p className="mt-1 text-sm text-foreground-muted">{helperText}</p>}
    </div>
  )
})

Input.displayName = 'Input'

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({
  label,
  error,
  id,
  className = '',
  ...props
}, ref) => {
  return (
    <div>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-foreground mb-1">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={id}
        className={`
          block w-full px-3 py-2 rounded border border-border
          text-foreground placeholder-foreground-muted
          bg-surface
          focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
          sm:text-sm
          ${error ? 'border-error' : ''}
          ${className}
        `}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-error">{error}</p>}
    </div>
  )
})

Textarea.displayName = 'Textarea'

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  required?: boolean
  options: { value: string; label: string }[]
  placeholder?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(({
  label,
  error,
  required,
  id,
  options,
  placeholder,
  className = '',
  ...props
}, ref) => {
  return (
    <div>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-foreground mb-1">
          {label}
          {required && <span className="text-error ml-0.5">*</span>}
        </label>
      )}
      <select
        ref={ref}
        id={id}
        className={`
          block w-full px-3 py-2 rounded border border-border
          text-foreground bg-surface
          focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
          sm:text-sm
          ${className}
        `}
        {...props}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {error && <p className="mt-1 text-sm text-error">{error}</p>}
    </div>
  )
})

Select.displayName = 'Select'
